import { useState, useEffect, useRef } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Send, Sparkles, MessageCircle, Bot } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import PaywallDialog from '@/components/PaywallDialog';
import { PromoBadge } from '@/components/PromoBanner';

const SUGGESTIONS = [
  'What\u2019s the right order to layer skincare actives?',
  'How can I reduce dark circles naturally?',
  'Best DIY remedies for hair fall in summer?',
  'How often should I exfoliate sensitive skin?',
  'What ingredients should I avoid during pregnancy?',
  'Tips for managing oily skin in humid weather?',
  'How to fade pigmentation without harsh actives?',
  'What to eat for naturally glowing skin?',
];

export default function AskPage() {
  const { user, setCreditsLocal, credits, promo } = useAuth();
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paywall, setPaywall] = useState({ open: false, required: 1, balance: 0 });
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const send = async (q) => {
    const text = (q ?? question).trim();
    if (!text) return;
    setMessages((m) => [...m, { role: 'user', text }]);
    setQuestion('');
    setLoading(true);
    try {
      const { data } = await api.post('/qa/ask', { question: text });
      if (typeof data?.credits === 'number') setCreditsLocal(data.credits);
      setMessages((m) => [...m, { role: 'assistant', text: data.answer }]);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 402 && detail?.error === 'insufficient_credits') {
        setPaywall({
          open: true,
          required: detail.required ?? 1,
          balance: detail.balance ?? 0,
        });
        // remove the placeholder user message? keep it to show context
        setMessages((m) => [
          ...m,
          {
            role: 'assistant',
            text: detail.message || 'You need more credits to ask a question.',
            error: true,
          },
        ]);
      } else {
        const msg = (typeof detail === 'string' ? detail : detail?.message) || 'Couldn\u2019t reach the AI. Please try again.';
        setMessages((m) => [...m, { role: 'assistant', text: msg, error: true }]);
        toast.error('AI request failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div data-testid="ask-page" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <div className="max-w-2xl">
        <span className="text-[11px] uppercase tracking-[0.22em] text-rose-gold flex items-center gap-2">
          <MessageCircle className="h-3.5 w-3.5" /> Ask Radiance
        </span>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-semibold text-ink leading-[1.05]">
          Your beauty questions, answered.
        </h1>
        <p className="mt-3 text-muted-foreground leading-relaxed">
          Ask anything skincare, haircare, makeup or wellness — our AI editor will respond warmly
          {user ? ', tailored to your profile.' : '. Sign in for answers tailored to your skin profile.'}
        </p>
        {user && (
          <div className="mt-3 flex items-center gap-2 text-xs flex-wrap">
            {promo?.active ? (
              <PromoBadge />
            ) : (
              <Badge className="bg-secondary text-foreground border border-border text-[10px] uppercase tracking-[0.14em]">
                1 credit per question
              </Badge>
            )}
            {!promo?.active && (
              <span className="text-muted-foreground tabular-nums">
                · Balance: <span className="font-semibold text-foreground">{credits}</span>
              </span>
            )}
          </div>
        )}
      </div>

      <Card className="mt-6 bg-card border-border/70 rounded-2xl flex flex-col h-[60vh] min-h-[420px]">
        <ScrollArea className="flex-1 p-5">
          {messages.length === 0 && !loading && (
            <div className="text-center py-6">
              <Sparkles className="h-9 w-9 text-rose-gold mx-auto" />
              <p className="mt-2 font-display text-2xl text-ink">Try a starter question</p>
              <p className="text-sm text-muted-foreground mt-1">Or type your own below.</p>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-2xl mx-auto">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="text-left text-sm rounded-xl border border-border/70 bg-cream-paper hover:bg-secondary/60 px-4 py-3 transition-colors"
                    data-testid="ask-suggestion"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-4">
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.role === 'user' ? 'justify-end' : ''}`}>
                {m.role === 'assistant' && (
                  <Avatar className="h-8 w-8 shrink-0">
                    <AvatarFallback className="bg-accent text-rose-gold border border-rose-tint">
                      <Bot className="h-4 w-4" />
                    </AvatarFallback>
                  </Avatar>
                )}
                <div
                  className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : m.error
                      ? 'bg-destructive/10 text-destructive border border-destructive/30'
                      : 'bg-secondary text-foreground'
                  }`}
                  data-testid={`ask-message-${m.role}`}
                >
                  {m.text}
                </div>
                {m.role === 'user' && (
                  <Avatar className="h-8 w-8 shrink-0">
                    <AvatarFallback className="bg-rose-tint text-ink text-xs font-semibold">
                      {(user?.name || 'You')[0].toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex gap-3">
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarFallback className="bg-accent text-rose-gold border border-rose-tint">
                    <Bot className="h-4 w-4" />
                  </AvatarFallback>
                </Avatar>
                <div className="bg-secondary rounded-2xl px-4 py-2.5 text-sm flex gap-1">
                  <span className="animate-pulse">●</span>
                  <span className="animate-pulse" style={{ animationDelay: '0.15s' }}>●</span>
                  <span className="animate-pulse" style={{ animationDelay: '0.3s' }}>●</span>
                </div>
              </div>
            )}
            <div ref={scrollRef} />
          </div>
        </ScrollArea>
        <div className="border-t border-border/70 p-3 sm:p-4">
          <div className="flex items-end gap-2">
            <Textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={onKey}
              placeholder="Ask anything beauty, skincare, hair, lifestyle..."
              className="min-h-[44px] max-h-[120px] rounded-xl bg-card resize-none"
              data-testid="ask-input"
            />
            <Button
              size="icon"
              onClick={() => send()}
              disabled={loading || !question.trim()}
              className="h-11 w-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="ask-send-button"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <p className="mt-2 text-[10px] text-muted-foreground text-center">
            Educational only. For medical concerns, please see a dermatologist.
          </p>
        </div>
      </Card>
    </div>
  );
}
